import { supabase } from './supabaseClient';
import arbolCatalog from '../data/arbol.json';

const CACHE_KEY = 'td_arbol_progreso';

/* ── Supabase queries (minimized for Free plan) ───── */

export async function fetchArbolData(userId) {
  // 1. Get negocio (1 query)
  const { data: negocio } = await supabase
    .from('arbol_negocios')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (!negocio) {
    return {
      negocio: null,
      mundos: arbolCatalog.mundos,
      misiones: arbolCatalog.misiones,
      progreso: [],
    };
  }

  // 2. Try localStorage cache first for instant render
  let progreso = [];
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    try { progreso = JSON.parse(cached); } catch {}
  }

  // 3. Fetch fresh from DB and update cache
  const { data } = await supabase
    .from('arbol_progreso')
    .select('*')
    .eq('negocio_id', negocio.id);
  progreso = data || [];
  localStorage.setItem(CACHE_KEY, JSON.stringify(progreso));

  return {
    negocio,
    mundos: arbolCatalog.mundos,
    misiones: arbolCatalog.misiones,
    progreso,
  };
}

export async function crearNegocio(userId, nombre, giro) {
  const slug =
    nombre
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') +
    '-' +
    Date.now().toString(36);

  const { data, error } = await supabase
    .from('arbol_negocios')
    .insert({ user_id: userId, nombre, giro, slug })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function validarMision(
  negocioId,
  misionId,
  modo,
  evidenciaUrl,
  evidenciaNota,
  evidenciaNivel
) {
  const { data, error } = await supabase
    .from('arbol_progreso')
    .insert({
      negocio_id: negocioId,
      mision_id: misionId,
      modo,
      evidencia_url: evidenciaUrl || null,
      evidencia_nota: evidenciaNota || null,
      evidencia_nivel: evidenciaNivel || 'nota',
    })
    .select()
    .single();

  if (error) throw error;

  // Update localStorage cache after successful insert
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const arr = cached ? JSON.parse(cached) : [];
    arr.push(data);
    localStorage.setItem(CACHE_KEY, JSON.stringify(arr));
  } catch {}

  return data;
}

/* ── Pure game logic (no DB) ──────────────────────── */

export function calcularEstado(mundos, misiones, progreso) {
  const progresoSet = new Set((progreso || []).map(p => p.mision_id));

  // Group main (non-side-quest) missions by world
  const mainByWorld = {};
  (misiones || []).forEach(m => {
    if (m.side_quest) return;
    if (!mainByWorld[m.mundo_id]) mainByWorld[m.mundo_id] = [];
    mainByWorld[m.mundo_id].push(m);
  });

  // Recursive mundo vencido check
  const vencidoCache = {};
  function mundoVencido(mId) {
    if (vencidoCache[mId] !== undefined) return vencidoCache[mId];
    const mainMissions = mainByWorld[mId] || [];
    const allDone =
      mainMissions.length > 0 && mainMissions.every(m => progresoSet.has(m.id));
    const prevOk = mId === 0 || mundoVencido(mId - 1);
    vencidoCache[mId] = allDone && prevOk;
    return vencidoCache[mId];
  }

  // Compute for all worlds
  const mundoVencidoMap = {};
  (mundos || []).forEach(w => {
    mundoVencidoMap[w.id] = mundoVencido(w.id);
  });

  // Active = first not vencido
  let mundoActivo = 0;
  for (const w of mundos || []) {
    if (!mundoVencidoMap[w.id]) {
      mundoActivo = w.id;
      break;
    }
    mundoActivo = w.id; // in case all are vencido
  }
  // If all vencido, mundoActivo stays at last
  if ((mundos || []).every(w => mundoVencidoMap[w.id])) {
    mundoActivo = (mundos || []).length - 1;
  }

  const nivel = Object.values(mundoVencidoMap).filter(Boolean).length;
  const pines = (progreso || []).length;

  // Stats per rama
  const stats = {};
  (progreso || []).forEach(p => {
    const m = (misiones || []).find(mi => mi.id === p.mision_id);
    if (m) stats[m.rama] = (stats[m.rama] || 0) + 1;
  });

  // Insignias from vencido worlds
  const insignias = (mundos || [])
    .filter(w => mundoVencidoMap[w.id])
    .map(w => w.insignia)
    .filter(Boolean);

  return { mundoVencidoMap, mundoActivo, nivel, pines, stats, insignias };
}
